import axios from "axios";
import { useEffect, useState } from "react";
import { mutate } from "swr";
import { Card, CardContent, Chip, Link } from "@mui/material";
import {
  generateNoDataValues,
  GetDateInDDMMYYYYFormat,
  getFiltersInfo,
} from "../../../common/utils";
import {
  getInventoryMovementHeaderFilteringOptions,
  getClearInventoryMovement,
  getMovementTypeColor,
  getMovementTypeName,
} from "../../../common/EntitiesManagement";
import ErrorMessage from "../../../components/common/ErrorMessage";
import ModalBackground from "../../../components/common/ModalBackground";
import PageTitleSubtitle from "../../../components/PageTitleSubTitle";
import localize from "../../../common/localize";
import SearcherAndAdd from "../../../components/SearcherAndAdd";
import TableComponent from "../../../components/common/TableComponent";
import PagePagination from "../../../components/PagePagination";
import ActionButtons from "../../../components/common/ActionButtons";
import ValueOrNoDataLabel from "../../../components/common/ValueOrNoDataLabel";
import useGlobalContext from "../../../hooks/useGlobalContext";
import AddInventoryMovement from "./AddInventoryMovement";
import PrinterModal from "../../../components/common/PrinterModal";
import { Print } from "@mui/icons-material";
import EntityPrinter from "../../../components/common/EntityPrinter";

export default function InventoryMovement() {
  const {
    inventoryMovementHeaderModel,
    setInventoryMovementHeaderModel,
    inventoryMovementHeaderData,
    INVENTORY_MOVEMENT_HEADER_ENDPOINT,
    config,
    pageIndexInventoryMovementHeader,
    setPageIndexInventoryMovementHeader,
    createEntity,
    setBasicFilterOptions,
    basicFilterData,
  } = useGlobalContext();
  const [
    inventoryMovementHeaderDataFiltered,
    setinventoryMovementHeaderDataFiltered,
  ] = useState(inventoryMovementHeaderData.Data);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPrintingModal, setShowPrintingModal] = useState(false);
  const [showPrinterModal, setShowPrinterModal] = useState(false);
  const [
    inventoryMovementDetailForPrinting,
    setInventoryMovementDetailForPrinting,
  ] = useState(null);

  useEffect(() => {
    setBasicFilterOptions(getInventoryMovementHeaderFilteringOptions());
  }, []);

  useEffect(() => {
    setinventoryMovementHeaderDataFiltered(inventoryMovementHeaderData.Data);
  }, [inventoryMovementHeaderData]);

  const validateOperationResult = (result, isDelete) => {
    setInventoryMovementHeaderModel(getClearInventoryMovement());

    if (!result.ok) {
      setErrorMessage(JSON.stringify(result.data));
      setShowErrorModal(true);
      return;
    }

    mutate([INVENTORY_MOVEMENT_HEADER_ENDPOINT, config]);

    if (isDelete) {
      if (
        pageIndexInventoryMovementHeader !== 0 &&
        inventoryMovementHeaderData.Data.length === 1
      ) {
        setPageIndexInventoryMovementHeader(
          pageIndexInventoryMovementHeader - 1
        );
      }
    }

    window.location.reload(false);
  };

  const handleCreateRequest = async (data) => {
    const movementDetail = data.MovementDetail.map((item) => ({
      ...item,
      MovementTypeId: data.MovementTypeId,
    }));

    const updateResult = await createEntity(
      `${`${INVENTORY_MOVEMENT_HEADER_ENDPOINT}`}`,
      {
        ...data,
        MovementDetail: JSON.stringify(movementDetail),
      },
      config
    );
    validateOperationResult(updateResult, false);
  };

  const disableSaveChanges = () =>
    !inventoryMovementHeaderModel?.MovementDate ||
    !inventoryMovementHeaderModel?.MovementDetail ||
    inventoryMovementHeaderModel?.MovementDetail.length === 0 ||
    inventoryMovementHeaderModel?.MovementTypeId === 0;

  const preparePrinting = async () => {
    try {
      const inventoryMovementDetail = await getInventoryMovementDetail();

      if (!inventoryMovementDetail || inventoryMovementDetail.length === 0) {
        setShowDetailModal(false);
        setErrorMessage(localize.no_data_to_print);
        setShowErrorModal(true);
        return;
      }

      setInventoryMovementDetailForPrinting(
        inventoryMovementDetail.map((detail) => [
          detail.Code ?? detail.SupplyElement.Code,
          detail.Name ?? detail.SupplyElement.Name,
          `${detail.Quantity} ${
            detail.Unit?.Symbol
              ? `(${detail.Unit?.Symbol})`
              : detail.Unit?.Name
              ? `${detail.Unit?.Name}`
              : ""
          }`,
          detail.Observation,
        ])
      );
      setShowDetailModal(false);
      setShowPrintingModal(true);
    } catch (error) {
      console.error("Error preparing printing for inventory movement", error);
      setShowDetailModal(false);
      setErrorMessage(localize.error_unexpected);
      setShowErrorModal(true);
    }
  };

  const getInventoryMovementDetail = async () => {
    const requestConfig = {
      ...config,
    };
    requestConfig.headers.pagination = -1;
    const data = await axios.get(
      `${INVENTORY_MOVEMENT_HEADER_ENDPOINT}/${inventoryMovementHeaderModel.Id}/detail`,
      requestConfig
    );
    return data?.data?.Data;
  };

  const getColumnTitles = () => {
    return [
      localize.movement_date,
      localize.code,
      localize.movement_type,
      localize.movement,
      localize.event,
      // localize.warehouse,
      localize.modification_date,
      localize.modified_by,
    ];
  };

  return (
    <div className="lateral_padding">
      {showErrorModal && (
        <ErrorMessage
          message={errorMessage}
          title={localize.error_unexpected}
          showModal={showErrorModal}
          setShowModal={setShowErrorModal}
        />
      )}

      {showPrinterModal && (
        <EntityPrinter
          closeCallback={() => setShowPrinterModal(false)}
          report_title={localize.inventory_movement}
          report_subtitle={getFiltersInfo(basicFilterData)}
          url={INVENTORY_MOVEMENT_HEADER_ENDPOINT}
          filters={basicFilterData ?? []}
          columns={getColumnTitles()}
          objectTypeName="inventory_movement_header"
        />
      )}

      {showPrintingModal && (
        <PrinterModal
          show={showPrintingModal}
          setShow={setShowPrintingModal}
          modalTitle={`${localize.print} - ${localize.inventory_movement}`}
          reportTitle={
            <>
              <p>
                {`${localize.inventory_movement} (${getMovementTypeName(
                  inventoryMovementHeaderModel?.MovementType?.MovementType
                )}) # ${inventoryMovementHeaderModel?.Id}`}
              </p>
              <p>{`${localize.code}: ${inventoryMovementHeaderModel.Code}`}</p>
              <p>{`${localize.movement_date}: ${GetDateInDDMMYYYYFormat(
                new Date(inventoryMovementHeaderModel.MovementDate)
              )}`}</p>
            </>
          }
          reportSubtitle={`${
            inventoryMovementHeaderModel?.EventRelated
              ? `${localize.service_order}: ${inventoryMovementHeaderModel?.EventRelated?.Code}\n`
              : ""
          }`}
          /*${
            inventoryMovementHeaderModel.Warehouse
              ? `${localize.warehouse}: ${inventoryMovementHeaderModel?.Warehouse?.Name}`
              : ""
          }`}*/
          data={inventoryMovementDetailForPrinting}
          columnHeaders={[
            localize.code,
            localize.name,
            localize.quantity,
            localize.observations,
          ]}
        />
      )}

      {showDetailModal && (
        <ModalBackground
          isDrawerMode
          hasRequiredValues
          showModal={showDetailModal}
          setShowModal={setShowDetailModal}
          title={`${localize.movement} #${inventoryMovementHeaderModel.Id}`}
          okButtonText={localize.print}
          onClickToSubmit={() => {
            preparePrinting();
          }}
          onClickToCancel={() => {
            setShowDetailModal(false);
            setInventoryMovementHeaderModel(getClearInventoryMovement());
          }}
          cancelButtonText={localize.close}
          Component={
            <AddInventoryMovement
              model={inventoryMovementHeaderModel}
              setModel={setInventoryMovementHeaderModel}
            />
          }
        />
      )}

      <Card
        variant="outlined"
        style={{
          margin: "16px",
          boxShadow:
            "rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 1px 3px 1px",
        }}
      >
        <CardContent>
          <PageTitleSubtitle
            title={localize.inventory_movement_title}
            subtitle={localize.inventory_movement_description}
          />

          <SearcherAndAdd
            printButtonMethod={() => {
              setShowPrinterModal(true);
            }}
            entityName="inventory_movement_header_filter"
            handleAddBtn={() => setShowAddModal(true)}
            modal={
              <ModalBackground
                isDrawerMode
                hasRequiredValues
                validators={disableSaveChanges()}
                showModal={showAddModal}
                setShowModal={setShowAddModal}
                title={localize.new_inventory_movement}
                onClickToSubmit={() => {
                  handleCreateRequest(inventoryMovementHeaderModel);
                  setShowAddModal(false);
                  setInventoryMovementHeaderModel(getClearInventoryMovement());
                }}
                onClickToCancel={() => {
                  setShowAddModal(false);
                  setInventoryMovementHeaderModel(getClearInventoryMovement());
                }}
                Component={
                  <AddInventoryMovement
                    model={inventoryMovementHeaderModel}
                    setModel={setInventoryMovementHeaderModel}
                  />
                }
              />
            }
          />
        </CardContent>
      </Card>

      <TableComponent
        pagination={
          <PagePagination
            pageIndex={pageIndexInventoryMovementHeader}
            setPageIndex={setPageIndexInventoryMovementHeader}
            dataArray={inventoryMovementHeaderData}
          />
        }
        firstColumnTitle={localize.actions}
        columnTitles={getColumnTitles()}
        results={
          inventoryMovementHeaderData.Data?.length !== 0
            ? inventoryMovementHeaderDataFiltered.map((item) => ({
                key: item.Id,
                data: [
                  <div>
                    <ActionButtons
                      buttons={[
                        {
                          name: localize.print_service_order,
                          icon: (
                            <Print style={{ width: "16px", height: "16px" }} />
                          ),
                          action: () => {
                            setInventoryMovementHeaderModel({
                              ...item,
                              MovementTypeId: item.MovementType?.Id,
                            });
                            preparePrinting();
                          },
                        },
                      ]}
                      detailsButtonText={localize.details}
                      onClickForDetailButton={() => {
                        setInventoryMovementHeaderModel({
                          ...item,
                          MovementTypeId: item.MovementType?.Id,
                        });
                        setShowDetailModal(true);
                      }}
                      hideEditButton
                      hideDeleteButton
                    />
                  </div>,
                  GetDateInDDMMYYYYFormat(new Date(item.MovementDate)),
                  <ValueOrNoDataLabel data={item.Code} />,
                  <ValueOrNoDataLabel data={item.MovementType.Name} />,
                  <Chip
                    className="chipContainer"
                    style={{
                      backgroundColor: getMovementTypeColor(
                        item.MovementType.MovementType
                      ),
                      color: "white",
                      borderColor: getMovementTypeColor(
                        item.MovementType.MovementType
                      ),
                    }}
                    label={getMovementTypeName(item.MovementType.MovementType)}
                  />,
                  !!item.EventRelated ? (
                    <Link
                      color="primary"
                      underline="always"
                      href="#"
                      onClick={() => {
                        window.open(
                          `/events/detail/${item.EventRelated.Id}`,
                          "_blank"
                        );
                      }}
                    >{`${item.EventRelated.Code}`}</Link>
                  ) : (
                    <ValueOrNoDataLabel data={null} />
                  ),
                  // <ValueOrNoDataLabel data={item.Warehouse?.Name} />,
                  new Date(item.ModificationDate).toLocaleString(undefined, {
                    year: "numeric",
                    month: "2-digit",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                    second: "2-digit",
                  }),
                  item.CreatedBy,
                ],
              }))
            : [generateNoDataValues(9)]
        }
      />
    </div>
  );
}
